import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Alert,
  AlertDescription,
  Button,
  EmptyState,
  ReportTable,
  type ReportTableColumn,
  type ReportTablePaginationState,
  type ReportTableSort,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import { ArrowLeft, Download } from 'lucide-react';
import { PermissionSlug } from '../constants/featureFlags';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import {
  exportPcrReport,
  getPcrReport,
  type PcrReportResult,
  type PcrReportRow,
  type PcrSortBy,
  type PcrStatusFilter,
  type SortDirection,
} from '../services/pcrReportApi';
import { downloadResponseAsFile } from '../utils/downloadFile';
import {
  getBusinessErrorMessage,
  getUserFacingApiMessageAsync,
} from '../utils/apiErrorDetail';
import {
  coerceReportPageSize,
  formatReportDate,
  formatReportEmptyValue,
  readChoiceParam,
  readOptionalChoiceParam,
  readPositiveIntegerParam,
  REPORT_PAGE_SIZE_OPTIONS,
} from '../utils/reportDisplay';
import { ReportLink } from '../components/reporting/ReportLink';
import { patientChartPath } from '../utils/navigation';
import ReportsPermissionAlert from './ReportsPermissionAlert';
import { useReportsBackHref } from '../reporting/backHref';

const STATUS_VALUES: readonly PcrStatusFilter[] = ['all', 'active', 'inactive'];
const SORT_VALUES: readonly PcrSortBy[] = [
  'clinic_name',
  'patient_last_name',
  'patient_first_name',
  'mrn',
  'admission_date',
  'episode_start_date',
  'episode_end_date',
  'episode_status',
  'episode_payor',
  'pcr_number_1',
  'pcr_number_2',
];
const DIRECTION_VALUES: readonly SortDirection[] = ['asc', 'desc'];

const text = (value: unknown) => formatReportEmptyValue(value);

const columns: ReportTableColumn<PcrReportRow>[] = [
  {
    id: 'clinic_name',
    header: 'Clinic',
    accessor: row => text(row.clinic_name),
    enableSorting: true,
    minWidth: 160,
  },
  {
    id: 'patient_first_name',
    header: 'Patient First',
    accessor: row => text(row.patient_first_name),
    render: (_value, row) => (
      <ReportLink to={patientChartPath(row.patient_id)}>
        {text(row.patient_first_name)}
      </ReportLink>
    ),
    enableSorting: true,
    minWidth: 150,
  },
  {
    id: 'patient_last_name',
    header: 'Patient Last',
    accessor: row => text(row.patient_last_name),
    render: (_value, row) => (
      <ReportLink to={patientChartPath(row.patient_id)}>
        {text(row.patient_last_name)}
      </ReportLink>
    ),
    enableSorting: true,
    minWidth: 150,
  },
  {
    id: 'admission_date',
    header: 'Admission Date',
    accessor: row => formatReportDate(row.admission_date),
    enableSorting: true,
    minWidth: 150,
  },
  {
    id: 'episode_start_date',
    header: 'Episode Start Date',
    accessor: row => formatReportDate(row.episode_start_date),
    enableSorting: true,
    minWidth: 170,
  },
  {
    id: 'episode_end_date',
    header: 'Episode End Date',
    accessor: row => formatReportDate(row.episode_end_date),
    enableSorting: true,
    minWidth: 160,
  },
  {
    id: 'episode_status',
    header: 'Episode Status',
    accessor: row => text(row.episode_status),
    enableSorting: true,
    minWidth: 150,
  },
  {
    id: 'episode_payor',
    header: 'Episode Payor',
    accessor: row => text(row.episode_payor),
    enableSorting: true,
    minWidth: 180,
  },
  {
    id: 'mrn',
    header: 'MRN',
    accessor: row => text(row.mrn),
    enableSorting: true,
    minWidth: 120,
  },
  {
    id: 'pcr_number_1',
    header: 'PCR Tracking #1',
    accessor: row => text(row.pcr_number_1),
    enableSorting: true,
    minWidth: 170,
  },
  {
    id: 'pcr_number_2',
    header: 'PCR Tracking #2',
    accessor: row => text(row.pcr_number_2),
    enableSorting: true,
    minWidth: 170,
  },
  {
    id: 'pcr_comment',
    header: 'PCR Comment',
    accessor: row => text(row.pcr_comment),
    enableSorting: false,
    minWidth: 220,
  },
];

const statusLabel: Record<PcrStatusFilter, string> = {
  all: 'All',
  active: 'Active',
  inactive: 'Inactive',
};

const PcrReport = () => {
  const { hasPermission } = useStore(
    useShallow(state => ({ hasPermission: state.hasPermission }))
  );
  const reportsBackHref = useReportsBackHref();
  const [searchParams, setSearchParams] = useSearchParams();
  const [report, setReport] = useState<PcrReportResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const status = readChoiceParam(
    searchParams.get('status'),
    STATUS_VALUES,
    'all'
  );
  const page = readPositiveIntegerParam(searchParams.get('page'));
  const pageSize = coerceReportPageSize(Number(searchParams.get('size')));
  const sortBy = readOptionalChoiceParam(searchParams.get('sort'), SORT_VALUES);
  const sortDirection = readOptionalChoiceParam(
    searchParams.get('dir'),
    DIRECTION_VALUES
  );

  const updateParams = useCallback(
    (updates: Record<string, string | null>, resetPage = true) => {
      setSearchParams(current => {
        const next = new URLSearchParams(current);
        Object.entries(updates).forEach(([key, value]) => {
          if (value) next.set(key, value);
          else next.delete(key);
        });
        if (resetPage) next.set('page', '1');
        return next;
      });
    },
    [setSearchParams]
  );

  useEffect(() => {
    if (!hasPermission(PermissionSlug.ROGER_EMR_REPORTS_ACCESS)) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    getPcrReport({
      status,
      sort_by: sortBy ?? null,
      sort_direction: sortDirection ?? null,
      page,
      page_size: pageSize,
    })
      .then(response => {
        if (cancelled) return;
        setReport(response.data);
      })
      .catch(caught => {
        console.error('Failed to load PCR report', caught);
        if (!cancelled) {
          setReport(null);
          setError(
            getBusinessErrorMessage(caught) ??
              'Unable to load PCR report. Please try again.'
          );
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [hasPermission, page, pageSize, sortBy, sortDirection, status]);

  const pagination: ReportTablePaginationState = useMemo(
    () => ({ page, pageSize }),
    [page, pageSize]
  );
  const sorting: ReportTableSort | null = sortBy
    ? { id: sortBy, direction: sortDirection ?? 'asc' }
    : null;

  if (!hasPermission(PermissionSlug.ROGER_EMR_REPORTS_ACCESS)) {
    return <ReportsPermissionAlert />;
  }

  const download = async () => {
    try {
      const response = await exportPcrReport({
        status,
        sort_by: sortBy ?? null,
        sort_direction: sortDirection ?? null,
      });
      downloadResponseAsFile(response, 'pcr-report.xlsx');
    } catch (caught) {
      console.error('Failed to download PCR report', caught);
      setError(
        await getUserFacingApiMessageAsync(
          caught,
          'Unable to download PCR report. Please try again.'
        )
      );
    }
  };

  return (
    <div className='space-y-6'>
      <Link
        to={reportsBackHref}
        className='inline-flex items-center text-sm font-medium text-muted-foreground hover:text-foreground'
      >
        <ArrowLeft className='mr-1 h-4 w-4' /> Reports
      </Link>
      <div className='flex flex-wrap items-start gap-3'>
        <Button
          variant='outline'
          size='sm'
          onClick={() => void download()}
          disabled={isLoading}
        >
          <Download className='h-4 w-4' /> Export XLSX
        </Button>
      </div>

      {error ? (
        <Alert className='border-red-200 bg-red-50'>
          <AlertDescription className='text-red-800'>{error}</AlertDescription>
        </Alert>
      ) : null}

      <div className='rounded-lg border bg-card p-4'>
        <label
          className='block max-w-sm space-y-1 text-sm font-medium'
          htmlFor='pcr-status-filter'
        >
          Episode Status
          <Select
            value={status}
            onValueChange={value =>
              updateParams({
                status: STATUS_VALUES.includes(value as PcrStatusFilter)
                  ? value
                  : 'all',
              })
            }
          >
            <SelectTrigger id='pcr-status-filter' selectSize='default'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_VALUES.map(value => (
                <SelectItem key={value} value={value}>
                  {statusLabel[value]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
      </div>

      <ReportTable
        data={report?.items ?? []}
        columns={columns}
        getRowId={row => row.row_id}
        caption='PCR report results'
        sorting={sorting}
        onSortingChange={next =>
          updateParams(
            next
              ? { sort: next.id, dir: next.direction }
              : { sort: null, dir: null }
          )
        }
        isLoading={isLoading}
        emptyState={
          <EmptyState
            title={error ?? 'No matching PCR rows'}
            description={
              error
                ? 'Please try again.'
                : 'Adjust the status filter and try again.'
            }
          />
        }
        className='min-w-[1600px] text-xs'
        pagination={{
          ...pagination,
          total: report?.total ?? 0,
          totalPages: Math.ceil((report?.total ?? 0) / pageSize),
          pageSizeOptions: REPORT_PAGE_SIZE_OPTIONS,
        }}
        onPaginationChange={next => {
          setSearchParams(current => {
            const params = new URLSearchParams(current);
            params.set('page', String(next.page));
            params.set('size', String(next.pageSize));
            return params;
          });
        }}
      />
    </div>
  );
};

export default PcrReport;
